<template>
  <transition name="modal-fade">
    <div
      v-overlay.autofocus="close"
      class="c-modal-backdrop"
      @mousedown="close"
      @keyup.escape="close"
    >
      <div
        class="c-modal"
        data-test="c-modal"
        @mousedown.stop
      >
        <CIcon
          v-if="showCloseIcon"
          class="text-gray-800 c-modal__close-icon"
          data-test="c-modal-close-button"
          name="close"
          size="medium"
          tabindex="0"
          @click="close"
          @keyup.enter="close"
        />
        <header
          v-if="hasHeaderSlot"
          class="c-modal__header"
        >
          <slot name="header" />
        </header>

        <div
          v-if="hasBodySlot"
          class="c-modal__body"
        >
          <slot name="body" />
        </div>

        <div
          v-if="hasFooterSlot"
          :class="
            hasFooterBorder ? 'border border-solid border-gray-300 p-5' : ''
          "
          class="c-modal__footer"
        >
          <slot name="footer" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount } from 'vue'
import CIcon from '../../atoms/CIcon'

type SlotName = 'header' | 'body' | 'footer'

interface Props {
  showCloseIcon?: boolean
  hasSlots?: { [key in SlotName]: boolean }
  shouldHideOverflow?: boolean
  hasFooterBorder?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showCloseIcon: true,
  hasSlots: {},
  shouldHideOverflow: true,
  hasFooterBorder: true,
})

const emit = defineEmits<{
  (e: 'close', event: Event): void
}>()

const hasHeaderSlot = computed(() => props.hasSlots.header)
const hasBodySlot = computed(() => props.hasSlots.body)
const hasFooterSlot = computed(() => props.hasSlots.footer)

const close = (e: Event) => {
  emit('close', e)
}

onMounted(() => {
  if (props.shouldHideOverflow) {
    document.getElementsByTagName('body')[0].classList.add('overflow-hidden')
  }
})

onBeforeUnmount(() => {
  if (props.shouldHideOverflow) {
    document.getElementsByTagName('body')[0].classList.remove('overflow-hidden')
  }
})
</script>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'CModal',
})
</script>
